import React, { useEffect, useRef, useState } from 'react';
import { coreFeatures, valueProposition } from '../data/mock';
import { Feather, Zap, Leaf, Sparkles } from 'lucide-react';

const iconMap = {
  Feather: Feather,
  Zap: Zap,
  Leaf: Leaf,
  Sparkles: Sparkles,
};

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative py-24 lg:py-32 bg-[#1e1919] overflow-hidden"
    >
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="text-[#c9a962] text-sm font-semibold tracking-[0.2em] uppercase">
            The Core
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
            A Revolutionary
            <span className="block gradient-text">Soy-Based Crispy Base</span>
          </h2>
          <p className="text-lg text-[#bbb5ae] leading-relaxed">
            Designed for modern nutrition without sacrificing indulgence. Experience
            the perfect balance of health and taste.
          </p>
        </div>

        {/* Core Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {coreFeatures.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            return (
              <div
                key={feature.id}
                className={`group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 transition-all duration-500 hover:bg-white/10 hover:border-[#c9a962]/30 hover:-translate-y-2 ${
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="w-14 h-14 bg-[#c9a962]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#c9a962]/20 transition-colors">
                  {IconComponent && (
                    <IconComponent className="w-7 h-7 text-[#c9a962]" />
                  )}
                </div>
                <h3 className="font-display text-2xl font-bold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#bbb5ae] leading-relaxed">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Pizza Box Showcase */}
        <div className={`mb-24 transition-all duration-1000 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* All 4 boxes in one row: Open, Closed, Closed, Open */}
            <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <img
                src="/images/box_open1.jpeg"
                alt="CRISPYzza open box with pizza"
                className="w-full aspect-square object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <img
                src="/images/box_closed1.jpeg"
                alt="CRISPYzza closed box"
                className="w-full aspect-square object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <img
                src="/images/box_closed2.jpeg"
                alt="CRISPYzza closed box"
                className="w-full aspect-square object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <img
                src="/images/box_open2.jpeg"
                alt="CRISPYzza open box with pizza"
                className="w-full aspect-square object-cover"
              />
            </div>
          </div>
        </div>

        {/* Value Proposition */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image */}
          <div
            className={`relative transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#c9a962]/20 to-transparent rounded-3xl blur-xl" />
              <img
                src="/images/image10.jpg"
                alt="CRISPYzza unique toppings"
                className="relative rounded-2xl w-full h-[450px] object-cover shadow-2xl"
              />
              {/* Overlay text */}
              <div className="absolute bottom-6 left-6 right-6 bg-[#1e1919]/90 backdrop-blur-sm rounded-xl p-6">
                <p className="text-[#c9a962] text-sm font-medium uppercase tracking-wider mb-1">
                  One Base
                </p>
                <p className="font-display text-2xl text-white font-bold">
                  Infinite Topping Stories
                </p>
              </div>
            </div>
          </div>

          {/* Right - Value Props */}
          <div
            className={`transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <h3 className="font-display text-3xl md:text-4xl font-bold text-white mb-10">
              Why CRISPYzza?
            </h3>
            <div className="space-y-6">
              {valueProposition.map((prop, index) => (
                <div
                  key={prop.id}
                  className="flex gap-6 group"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-[#c9a962] rounded-full flex items-center justify-center text-[#1e1919] font-bold group-hover:scale-110 transition-transform">
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  <div>
                    <h4 className="font-display text-xl font-bold text-white mb-2">
                      {prop.title}
                    </h4>
                    <p className="text-[#bbb5ae]">{prop.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
